import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import CustomStore from "devextreme/data/custom_store";
import { API } from "../api";
import { PersonalNotificationDto } from "../dtos/personal-notifications-dto";

interface NotificationResponse {
	content: PersonalNotificationDto[];
	totalElements: number;
	totalPages: number;
	size: number;
	number: number;
}

const getPaginatedPersonalNotificationsByParameters = (
	page: number,
	size: number,
	sort = "generationDate,DESC",
	showLoading = false,
	unreadNotifications = false,
) => {
	const store = new CustomStore({
		key: "id",
		load() {
			return ItiHttpService.get(
				`${API.notifications.personal}?page=${page}&size=${size}&sort=${sort}${
					unreadNotifications ? "&status=UNREAD" : ""
				}`,
				null,
				showLoading,
			).then((response) => ({
				data: response.content,
				totalCount: response.totalElements,
			}));
		},
	});

	return store;
};

export const NotificationService = {
	async getPaginatedPersonalNotificationsWithMultiSort(
		page: number,
		size: number,
		sortParams: string[],
		includeRead: boolean,
		loading = true,
	): Promise<any> {
		let url = `${API.notifications.personal}?page=${page}&size=${size}`;

		// Añadimos múltiples parámetros de ordenación
		for (const sort of sortParams) {
			url += `&sort=${sort}`;
		}

		if (includeRead !== undefined) {
			url += `&includeRead=${includeRead}`;
		}

		const response = await ItiHttpService.get(url, null, loading);

		return response;
	},

	async getUnreadPersonalNotifications(): Promise<PersonalNotificationDto[]> {
		const res = await ItiHttpService.get(
			`${API.notifications.personal}?status=UNREAD`,
			null,
			false,
		);
		return res.content.map(
			(item: PersonalNotificationDto) => new PersonalNotificationDto(item),
		);
	},

	// Servicio para obtener notificaciones personales no leídas paginadas
	async getPaginatedUnreadPersonalNotifications(
		page: number,
		size: number,
		sort = "generationDate,DESC",
		showLoading = false,
	) {
		return getPaginatedPersonalNotificationsByParameters(
			page,
			size,
			sort,
			showLoading,
			true,
		);
	},

	async getPersonalNotifications(
		showLoading = false,
	): Promise<PersonalNotificationDto[]> {
		const res = await ItiHttpService.get(
			`${API.notifications.personal}`,
			null,
			showLoading,
		);
		return res.content.map(
			(item: PersonalNotificationDto) => new PersonalNotificationDto(item),
		);
	},

	async getPaginatedPersonalNotifications(
		page: number,
		size: number,
		sort = "generationDate,DESC",
		showLoading = false,
	) {
		return getPaginatedPersonalNotificationsByParameters(
			page,
			size,
			sort,
			showLoading,
		);
	},

	async getPaginatedPersonalNotificationsPaginated(
		page: number,
		size: number,
		sort = "generationDate,DESC",
		showLoading = false,
	): Promise<NotificationResponse> {
		const params: any = {};

		if (page !== undefined && !Number.isNaN(page)) params.page = page;
		if (size !== undefined) {
			params.size = size;
		} else {
			params.size = localStorage.getItem("notificationsCount") || 10;
		}
		if (sort !== undefined) params.sort = sort;

		const queryString = Object.entries(params)
			.map(([key, value]) => `${key}=${value}`)
			.join("&");

		const url = `${API.notifications.personal}${queryString ? `?${queryString}` : ""}`;

		const response = await ItiHttpService.get(url, null, showLoading);

		localStorage.setItem("notificationsCount", response.totalElements);

		return {
			...response,
			content: response.content.map(
				(item: any) => new PersonalNotificationDto(item),
			),
		};
	},

	async getPersonalNewNotifications(): Promise<PersonalNotificationDto[]> {
		const res = await ItiHttpService.get(
			`${API.notifications.personal}/new`,
			null,
			false,
		);
		return res.content.map(
			(item: PersonalNotificationDto) => new PersonalNotificationDto(item),
		);
	},
};
